import React, { useState } from 'react';
import { Container, Row, Col, Image, Button, Alert } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import CommunityIcon from './CommunityIcon';

// Descriptions longer than this get a Read more/less toggle so long text
// isn't a wall of text on narrow/vertical screens. Shorter descriptions
// render in full with no toggle.
const DESCRIPTION_TRUNCATE_LENGTH = 300;

const CommunityHeader = ({
    icon,
    title,
    subtitle,
    description,
    memberCount,
    onJoinClick,
    onMakePost,
    isMember = false,
    isAdmin = false,
    user = null,
    onDeleteClick,
    canApplyToModerate = false,
    onApplyToModerate,
    latestApplication = null,
}) => {
    const location = useLocation();
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const applicationDismissKey = latestApplication?._id
        ? `dismissed-app-${latestApplication._id}`
        : null;
    const [indicatorDismissed, setIndicatorDismissed] = useState(
        () =>
            !!applicationDismissKey &&
            !!localStorage.getItem(applicationDismissKey)
    );

    const handleDismissIndicator = () => {
        if (applicationDismissKey) {
            localStorage.setItem(applicationDismissKey, 'true');
        }
        setIndicatorDismissed(true);
    };

    const isLongDescription =
        typeof description === 'string' &&
        description.length > DESCRIPTION_TRUNCATE_LENGTH;
    const isDescriptionCollapsed = isLongDescription && !isDescriptionExpanded;

    return (
        <Container className="community-header-section py-4">
            <Row className="align-items-start">
                <Col xs="auto">
                    {icon ? (
                        <Image
                            src={icon}
                            alt={`${title} icon`}
                            roundedCircle
                            className="community-icon"
                        />
                    ) : (
                        <CommunityIcon
                            name={title}
                            className="community-icon rounded-circle"
                        />
                    )}
                </Col>
                <Col>
                    <h1 className="community-title mb-2">{title}</h1>
                    {subtitle && (
                        <p className="community-subtitle text-muted mb-3">
                            {subtitle}
                        </p>
                    )}
                    <p
                        id="community-header-description"
                        className={`community-description${
                            isDescriptionCollapsed
                                ? ' community-description-collapsed'
                                : ''
                        }`}
                    >
                        {description}
                    </p>
                    {isLongDescription && (
                        <Button
                            variant="link"
                            className="p-0 mb-3 text-dark"
                            onClick={() =>
                                setIsDescriptionExpanded((prev) => !prev)
                            }
                            aria-expanded={isDescriptionExpanded}
                            aria-controls="community-header-description"
                        >
                            {isDescriptionExpanded
                                ? 'Collapse description'
                                : 'View full description'}
                        </Button>
                    )}
                    {memberCount && (
                        <p className="community-members text-muted mb-3">
                            {memberCount} members
                        </p>
                    )}
                    <div className="d-flex gap-2 flex-wrap">
                        {user && isMember && (
                            <Button
                                variant="primary"
                                onClick={onMakePost}
                                className="community-make-post-button px-3 py-2 fw-medium rounded-pill text-center"
                            >
                                Make a Post
                            </Button>
                        )}
                        {user ? (
                            <Button
                                variant={
                                    isMember ? 'outline-primary' : 'primary'
                                }
                                onClick={onJoinClick}
                                className="community-join-button px-3 py-2 fw-medium rounded-pill text-center"
                            >
                                {isMember ? 'Unfollow' : 'Join'}
                            </Button>
                        ) : (
                            <Button
                                as={Link}
                                to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
                                variant="primary"
                                className="community-join-button px-3 py-2 fw-medium rounded-pill text-center"
                            >
                                Log in to join
                            </Button>
                        )}
                        {isAdmin && (
                            <Button
                                variant="outline-danger"
                                onClick={onDeleteClick}
                                className="community-delete-button px-3 py-2 fw-medium rounded-pill text-center"
                            >
                                Delete Community
                            </Button>
                        )}
                        {canApplyToModerate && (
                            <Button
                                variant="outline-success"
                                onClick={onApplyToModerate}
                                className="community-apply-to-moderate-button px-3 py-2 fw-medium rounded-pill text-center"
                            >
                                Apply to Moderate
                            </Button>
                        )}
                    </div>
                    {latestApplication &&
                        !indicatorDismissed &&
                        (latestApplication.status === 'accepted' ||
                            latestApplication.status === 'rejected') && (
                            <Alert
                                variant={
                                    latestApplication.status === 'accepted'
                                        ? 'success'
                                        : 'warning'
                                }
                                className="mt-3 mb-0"
                                data-testid="application-outcome"
                                dismissible
                                onClose={handleDismissIndicator}
                            >
                                {latestApplication.status === 'accepted' ? (
                                    <span>
                                        Congratulations, your moderator
                                        application has been accepted. You are
                                        now a moderator in this community!
                                    </span>
                                ) : (
                                    <>
                                        <span>
                                            Your moderator application was
                                            rejected. You may apply again in 1
                                            day(s).
                                        </span>
                                        {latestApplication.decisionReason && (
                                            <span className="d-block mt-1">
                                                <strong>Reason:</strong>{' '}
                                                {
                                                    latestApplication.decisionReason
                                                }
                                            </span>
                                        )}
                                    </>
                                )}
                            </Alert>
                        )}
                </Col>
            </Row>
        </Container>
    );
};

CommunityHeader.propTypes = {
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    description: PropTypes.string.isRequired,
    memberCount: PropTypes.string,
    onJoinClick: PropTypes.func.isRequired,
    onMakePost: PropTypes.func.isRequired,
    isMember: PropTypes.bool,
    isAdmin: PropTypes.bool,
    user: PropTypes.object,
    onDeleteClick: PropTypes.func,
    canApplyToModerate: PropTypes.bool,
    onApplyToModerate: PropTypes.func,
    latestApplication: PropTypes.shape({
        _id: PropTypes.string,
        status: PropTypes.oneOf(['accepted', 'rejected']),
        decisionReason: PropTypes.string,
    }),
};

export default CommunityHeader;
